import { createSignal, createEffect, onMount, Show, For } from 'solid-js';
import { selfServiceApi } from '@/services/selfservice';
import type { LeaderboardEntry, ComparisonStatsResponse } from '@/services/selfservice';
import { gameStore } from '@/stores/gameStore';

export default function SelfserviceRanking() {
  const [leaderboard, setLeaderboard] = createSignal<LeaderboardEntry[]>([]);
  const [totalEntries, setTotalEntries] = createSignal(0);
  const [comparisonStats, setComparisonStats] = createSignal<ComparisonStatsResponse | null>(null);
  const [loading, setLoading] = createSignal(true);
  const [error, setError] = createSignal<string | null>(null);

  const getSessionId = () => parseInt(localStorage.getItem('selfservice_session_id') || '0');
  const getTemplateId = () => parseInt(localStorage.getItem('selfservice_template_id') || '0');
  const getAccessCode = () => localStorage.getItem('selfservice_access_code') || '';

  const getElapsedSeconds = () => {
    const total = gameStore.gameState.game?.duration;
    const remaining = gameStore.timeRemaining();
    if (!total) return 0;
    // Parse duration string "HH:MM:SS" to seconds
    const parts = total.split(':').map(Number);
    const totalSec = (parts[0] || 0) * 3600 + (parts[1] || 0) * 60 + (parts[2] || 0);
    return Math.max(0, totalSec - remaining);
  };

  const getCorrectAnswers = () => {
    return gameStore.gameState.progress.filter(p => p.status === 'correct').length;
  };

  const loadData = async () => {
    const templateId = getTemplateId();
    const sessionId = getSessionId();
    const code = getAccessCode();

    if (!templateId || !sessionId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const [lbResult, statsResult] = await Promise.allSettled([
        selfServiceApi.getLeaderboard(templateId, {
          sessionType: 'solo',
          limit: 5,
          code
        }),
        selfServiceApi.getComparisonStats(
          sessionId,
          code,
          getElapsedSeconds(),
          getCorrectAnswers()
        )
      ]);

      if (lbResult.status === 'fulfilled') {
        setLeaderboard(lbResult.value.ranking);
        setTotalEntries(lbResult.value.total_entries);
      }

      if (statsResult.status === 'fulfilled') {
        setComparisonStats(statsResult.value);
      }
    } catch (err) {
      setError('Nie udało się pobrać danych rankingu');
    } finally {
      setLoading(false);
    }
  };

  onMount(() => {
    loadData();
  });

  // Refresh when progress changes
  let prevCorrect = 0;
  createEffect(() => {
    const correct = getCorrectAnswers();
    if (correct !== prevCorrect && prevCorrect > 0) {
      loadData();
    }
    prevCorrect = correct;
  });

  const getMedal = (position: number) => {
    switch (position) {
      case 1: return '🥇';
      case 2: return '🥈';
      case 3: return '🥉';
      default: return `#${position}`;
    }
  };

  const formatTime = (timeStr: string) => {
    // Remove microseconds
    return timeStr.split('.')[0];
  };

  const insufficientData = () => {
    return totalEntries() < 3 && (!comparisonStats() || comparisonStats()!.insufficient_data);
  };

  return (
    <div class="space-y-4">
      {/* Loading */}
      <Show when={loading()}>
        <div class="flex items-center justify-center py-8">
          <div class="animate-spin h-8 w-8 border-2 border-emerald-400 border-t-transparent rounded-full" />
        </div>
      </Show>

      {/* Error */}
      <Show when={error()}>
        <div class="text-center py-4 text-red-400">
          <p>{error()}</p>
          <button onClick={loadData} class="mt-2 text-sm text-emerald-400 hover:underline">
            Spróbuj ponownie
          </button>
        </div>
      </Show>

      <Show when={!loading() && !error()}>
        {/* Insufficient data message */}
        <Show when={insufficientData()}>
          <div class="text-center py-8 space-y-3">
            <div class="text-4xl">🌟</div>
            <h3 class="text-lg font-bold text-white">Bądź jednym z pierwszych!</h3>
            <p class="text-sm text-gray-400">
              Za mało zespołów ukończyło tę grę, aby pokazać ranking.
              Ukończ grę i ustal swój czas!
            </p>
            <Show when={totalEntries() > 0}>
              <p class="text-xs text-gray-500">
                Dotychczas: {totalEntries()} {totalEntries() === 1 ? 'zespół' : totalEntries() < 5 ? 'zespoły' : 'zespołów'}
              </p>
            </Show>
          </div>
        </Show>

        {/* Statistics + Leaderboard */}
        <Show when={!insufficientData()}>
          {/* Overall stats */}
          <Show when={comparisonStats()?.overall}>
            <div class="grid grid-cols-3 gap-3">
              <div class="bg-black/20 rounded-xl p-3 text-center">
                <div class="text-xs text-gray-400 mb-1">Najlepszy czas</div>
                <div class="text-sm font-bold text-emerald-400">
                  {comparisonStats()!.overall!.best_time_formatted}
                </div>
              </div>
              <div class="bg-black/20 rounded-xl p-3 text-center">
                <div class="text-xs text-gray-400 mb-1">Średni czas</div>
                <div class="text-sm font-bold text-yellow-400">
                  {comparisonStats()!.overall!.avg_time_formatted}
                </div>
              </div>
              <div class="bg-black/20 rounded-xl p-3 text-center">
                <div class="text-xs text-gray-400 mb-1">Zespołów</div>
                <div class="text-sm font-bold text-white">
                  {comparisonStats()!.total_teams}
                </div>
              </div>
            </div>
          </Show>

          {/* TOP 5 Leaderboard */}
          <Show when={leaderboard().length > 0}>
            <div class="mt-4">
              <h4 class="text-sm font-semibold text-gray-300 mb-3 flex items-center gap-2">
                🏆 TOP 5
              </h4>
              <div class="space-y-2">
                <For each={leaderboard()}>
                  {(entry) => (
                    <div class={`flex items-center gap-3 py-2 px-3 rounded-lg ${
                      entry.is_current_team
                        ? 'bg-emerald-500/10 border border-emerald-500/30'
                        : 'bg-black/10'
                    }`}>
                      <div class="w-8 text-center text-lg">
                        {getMedal(entry.position)}
                      </div>
                      <div class="flex-1 min-w-0">
                        <div class={`text-sm font-medium truncate ${
                          entry.is_current_team ? 'text-emerald-400' : 'text-white'
                        }`}>
                          {entry.team_name}
                          <Show when={entry.is_current_team}>
                            <span class="text-xs ml-1 text-emerald-300">(Wy)</span>
                          </Show>
                        </div>
                        <div class="text-xs text-gray-500">
                          {entry.correct_answers}/{entry.total_tiles} zagadek
                          {entry.completed_all && entry.stopped_clock && ' ✓'}
                        </div>
                      </div>
                      <div class="text-right">
                        <div class={`text-sm font-mono ${
                          entry.is_current_team ? 'text-emerald-400' : 'text-white'
                        }`}>
                          {formatTime(entry.total_time)}
                        </div>
                      </div>
                    </div>
                  )}
                </For>
              </div>
            </div>
          </Show>

          {/* Estimated position */}
          <Show when={comparisonStats() && !comparisonStats()!.insufficient_data && comparisonStats()!.estimated_percentile != null}>
            <div class="mt-4 bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 rounded-xl p-4 border border-emerald-500/20">
              <div class="text-center">
                <div class="text-xs text-gray-400 mb-1">Szacowana pozycja</div>
                <div class="text-lg font-bold text-emerald-400">
                  TOP {comparisonStats()!.estimated_percentile}%
                </div>
                <div class="text-xs text-gray-500 mt-1">
                  Na podstawie aktualnego tempa gry
                </div>
              </div>
            </div>
          </Show>
        </Show>

        {/* Refresh button */}
        <div class="text-center pt-2">
          <button
            onClick={loadData}
            class="text-xs text-gray-500 hover:text-emerald-400 transition-colors"
          >
            🔄 Odśwież ranking
          </button>
        </div>
      </Show>
    </div>
  );
}
